import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAthleteAdditionalData,
  setLoadingAthleteAdditional,
  setErrorAthleteAdditional,
} from "../../../store/slices/additionalInfoSlice";
import { getAdditionalInfo } from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import LoadingSpinner from "../../LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import ReactPlayer from "react-player";
import { profileVideos } from "../../../utils/profileVideo";
import { faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { HoverCard } from "./hoverCard";
import InstagramPreview from "./instagramPrew";

export default function AthleteAdditionalInfo({ userId }) {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.additionalInfo);
  const apiInstance = useAxiosInstance();
  const [openIndex, setOpenIndex] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState(null);

  const toggleAccordion = (index) => {
    setPlayingVideoId(null);
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      dispatch(setLoadingAthleteAdditional(true));
      try {
        const response = await getAdditionalInfo(apiInstance, userId);
        dispatch(setAthleteAdditionalData(response));
      } catch (err) {
        dispatch(setErrorAthleteAdditional(err.message));
      } finally {
        dispatch(setLoadingAthleteAdditional(false));
      }
    };

    fetchData();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkLabels = {
    hudlFilmUrl: "HUDL Film",
    basketballHighlightsUrl: "Basketball Highlights",
    track100mVideoUrl: "Track 100m Video",
    sevenOnSevenHighlightsUrl: "7-on-7 Highlights",
    oneOnOnesVideoUrl: "One-on-Ones Video",
  };

  function titleVideo(data) {
    const video = profileVideos.find((v) => v.id === data.type);
    return video?.name || "No Title";
  }

  const accordionData = useMemo(() => {
    if (!data) return [];

    return [
      {
        title: "College and Eligibility",
        content: (
          <>
            <div className="grid grid-cols-1">
              {data?.collegeInfo.universitiesAttended && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3">
                  {data.collegeInfo.universitiesAttended.map(
                    ({ year, school, className }) => (
                      <div
                        key={`${year}-${school}`}
                        className="text-base font-medium text-gray-700"
                      >
                        <div>Year: {year === 0 ? "" : year}</div>
                        <div>School: {school}</div>
                        <div>Class: {className}</div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1">
              {data?.collegeInfo && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3">
                  <div className="text-base font-medium text-gray-700">
                    Graduated: {data.collegeInfo.hasGraduated ? "Yes" : "No"}
                  </div>
                  <div className="text-base font-medium text-gray-700">
                    Major: {data.collegeInfo.major || ""}
                  </div>
                  <div className="text-base font-medium text-gray-700">
                    GPA:
                  </div>
                  <div className="text-base font-medium text-gray-700">
                    Years of Eligibility Left:{" "}
                    {data.collegeInfo.yearsLeftToPlay ?? ""}
                  </div>
                  <div className="text-base font-medium text-gray-700">
                    Redshirt Available:{" "}
                    {data.collegeInfo.redShirtAvailable ? "Yes" : ""}
                  </div>
                </div>
              )}
            </div>
          </>
        ),
      },
      {
        title: "Person Helping With Your Decision",
        content: (
          <div className="grid grid-cols-1">
            {data?.collegeInfo && (
              <div className="text-gray-800 mb-4 py-2 flex flex-col">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-base font-medium text-gray-700">
                    Name: {data?.collegeInfo.personHelpingYouName}
                  </div>
                  <div className="text-base font-medium text-gray-700">
                    Relationship:{" "}
                    {data?.collegeInfo.personHelpingYouRelationship}
                  </div>
                  <div className="text-base font-medium text-gray-700">
                    Phone: {data?.collegeInfo.personHelpingYouPhone}
                  </div>
                </div>
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Personal Bio",
        content: <p className="text-gray-700">{data.bio}</p>,
        defaultOpen: true,
      },
      {
        title: "Profile Videos",
        content: (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6 mx-8 justify-items-center">
            {data.profileVideos?.map((video, index) => (
              <div
                key={video.id ?? index}
                className="flex flex-col items-center"
              >
                <div className="w-[250px] h-[200px] bg-black rounded-md overflow-hidden">
                  <ReactPlayer
                    controls
                    light={true}
                    src={video.filePath}
                    playsInline={true}
                    height={200}
                    width={250}
                    className="rounded-md"
                    playing={playingVideoId === video.filePath}
                    onPlay={() => setPlayingVideoId(video.filePath)}
                    onPause={() =>
                      setPlayingVideoId((cur) =>
                        cur === video.filePath ? null : cur,
                      )
                    }
                    onEnded={() => setPlayingVideoId(null)}
                  />
                </div>
                <span className="pt-2 text-center font-medium">
                  {titleVideo(video)}
                </span>
              </div>
            ))}
          </div>
        ),
      },
      {
        title: "Weight Room Videos",
        content: (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6">
            {data.weightRoomVideos
              ?.filter((video) => video.filePath)
              .map((video, index) => (
                <div key={video.id ?? index}>
                  <div className="w-[250px] h-[200px] bg-black">
                    <ReactPlayer
                      controls
                      light={true}
                      src={video.filePath}
                      playsInline={true}
                      height={150}
                      width={250}
                      className="rounded-md"
                      playing={playingVideoId === video.filePath}
                      onPlay={() => setPlayingVideoId(video.filePath)}
                      onPause={() =>
                        setPlayingVideoId((cur) =>
                          cur === video.filePath ? null : cur,
                        )
                      }
                      onEnded={() => setPlayingVideoId(null)}
                    />
                  </div>
                  <span className="block font-medium pt-2">
                    {video.title} - {video.weight} Pounds x {video.reps}
                  </span>
                </div>
              ))}
          </div>
        ),
      },
      {
        title: "Video Links",
        content: (
          <div className="space-y-3">
            {Object.entries(data.profileVideoLinks).map(([key, url]) =>
              url ? (
                <div key={key}>
                  <span className="font-medium">{linkLabels[key] || key}:</span>
                  <span className="block">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {url}
                    </a>
                  </span>
                </div>
              ) : null,
            )}
          </div>
        ),
      },
    ];
  }, [data, playingVideoId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading)
    return (
      <div className="mt-4 text-center text-lg font-semibold">
        <LoadingSpinner />
      </div>
    );

  if (error)
    return (
      <p className="text-center text-red-500 font-semibold">Error: {error}</p>
    );

  return (
    <>
      <div className="pt-0 px-4 -m-4 mt-0 overflow-y-auto pb-2 xl:pb-0 lg:pb-0">
        <div className="mt-3 text-right">
          <span className="border border-red-500 rounded-md p-2 text-gray-800 text-sm inline-flex">
            <img
              className="h-5 mr-2"
              alt="Not Verified"
              src="/notverified.svg"
            />{" "}
            Not verified.
          </span>
        </div>

        <div className="mt-0 space-y-2">
          {data?.contact && (
            <div className="text-gray-800 mb-6">
              <h2 className="text-md font-bold mb-4 border-b pb-2">
                Contact Info
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    icon: faInstagram,
                    label: "Instagram",
                    value: data.contact.instagram,
                    link: data.contact.instagram
                      ? `https://instagram.com/${data.contact.instagram}`
                      : null,
                    type: "instagram",
                  },
                  {
                    icon: faXTwitter,
                    label: "X",
                    value: data.contact.twitter,
                    link: data.contact.twitter
                      ? `https://twitter.com/${data.contact.twitter}`
                      : null,
                  },
                  {
                    icon: faEnvelope,
                    label: "Athlete Email",
                    value: data.contact.athleteEmail,
                    link: data.contact.athleteEmail
                      ? `mailto:${data.contact.athleteEmail}`
                      : null,
                  },
                  {
                    icon: faEnvelope,
                    label: "Parent Email",
                    value: data.contact.parentEmail,
                    link: data.contact.parentEmail
                      ? `mailto:${data.contact.parentEmail}`
                      : null,
                  },
                  {
                    icon: faPhone,
                    label: "Athlete Phone",
                    value: data.contact.athletePhone,
                  },
                  {
                    icon: faPhone,
                    label: "Parent Phone",
                    value: data.contact.parentPhone,
                  },
                ].map(({ icon, label, value, link, type }) => {
                  const content = (
                    <div className="flex items-center gap-3 p-2 border rounded-lg shadow-sm">
                      <FontAwesomeIcon
                        icon={icon}
                        className="text-gray-400 w-5"
                      />
                      <span className="font-medium">{label}:</span>
                      {link ? (
                        <a
                          href={link}
                          className="text-blue-600 hover:underline"
                        >
                          {value || "N/A"}
                        </a>
                      ) : (
                        <span>{value || "N/A"}</span>
                      )}
                    </div>
                  );
                  if (type === "instagram" && value) {
                    return (
                      <HoverCard key={label} trigger={content}>
                        <InstagramPreview username={data.contact.instagram} />
                      </HoverCard>
                    );
                  }
                  return <div key={label}>{content}</div>;
                })}
              </div>
            </div>
          )}

          {data?.profileVideos?.some(
            (video) => titleVideo(video) === "Hudl Film",
          ) ? (
            <div className="mb-6 pb-4">
              <h2 className="text-lg font-bold mb-4 border-b pb-2">
                🎥 Hudl Film
              </h2>
              <ReactPlayer
                controls
                src={
                  data.profileVideos.find(
                    (video) => titleVideo(video) === "Hudl Film",
                  ).filePath
                }
                width="100%"
                height="300px"
                className="rounded-md shadow-md"
                playing={playingVideoId === "hudl-top"}
                onPlay={() => setPlayingVideoId("hudl-top")}
                onPause={() =>
                  setPlayingVideoId((cur) => (cur === "hudl-top" ? null : cur))
                }
                onEnded={() => setPlayingVideoId(null)}
              />
            </div>
          ) : data?.profileVideoLinks?.hudlFilmUrl ? (
            <div className="mb-6 pb-4">
              <h2 className="text-2xl font-bold mb-4 border-b pb-2">
                🎥 Hudl Film Link
              </h2>
              <a
                href={data.profileVideoLinks.hudlFilmUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {data.profileVideoLinks.hudlFilmUrl}
              </a>
            </div>
          ) : null}

          {accordionData.map((item, index) => (
            <div key={index} className="border rounded shadow-sm">
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full flex justify-between items-center px-4 py-3 font-semibold bg-gray-100 hover:bg-gray-200 focus:outline-none"
              >
                <span>{item.title}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`transition-transform duration-300 ${openIndex === index ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-auto transition-all duration-300 bg-white ${
                  openIndex === index ? "p-4" : "max-h-0 p-0"
                }`}
              >
                <div className="text-sm">{item.content}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
